// Photos are resized on the device before they're encrypted and sent
// (docs/photo-uploads.md, D135): a camera photo went up at full size, slow
// on a weak connection and easily cut off (2026-10-22).

/** The longest side a photo is sent at. */
export const PHOTO_MAX_SIDE = 1600;
const JPEG_QUALITY = 0.82;

/** The size to send at, or null when the photo already fits. */
export function fitWithin(
  width: number,
  height: number,
  max = PHOTO_MAX_SIDE
): { width: number; height: number } | null {
  if (width <= 0 || height <= 0) return null;
  const scale = max / Math.max(width, height);
  if (scale >= 1) return null;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/** The new file name for a photo re-encoded as `type`. */
export function renamed(fileName: string, type: string): string {
  const extension = type === 'image/png' ? 'png' : 'jpg';
  const stem = fileName.replace(/\.[^./]+$/, '') || 'photo';
  return `${stem}.${extension}`;
}

/**
 * The photo to send: at most PHOTO_MAX_SIDE on its longest side. PNGs
 * (screenshots, drawings) stay PNG so text stays sharp; everything else
 * becomes JPEG. Re-encoding also drops hidden metadata such as GPS. The
 * original is sent as-is when it's a GIF, already fits, can't be decoded
 * here (e.g. HEIC in Chrome), or wouldn't get smaller.
 */
export async function resizePhoto(file: File): Promise<File> {
  if (file.type === 'image/gif') return file;
  const url = URL.createObjectURL(file);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error('Unreadable image'));
      element.src = url;
    });
    const target = fitWithin(image.naturalWidth, image.naturalHeight);
    if (!target) return file;

    const canvas = document.createElement('canvas');
    canvas.width = target.width;
    canvas.height = target.height;
    const context = canvas.getContext('2d');
    if (!context) return file;
    context.imageSmoothingQuality = 'high';
    context.drawImage(image, 0, 0, target.width, target.height);

    const type = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(
        resolve,
        type,
        type === 'image/jpeg' ? JPEG_QUALITY : undefined
      )
    );
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], renamed(file.name, type), {
      type,
      lastModified: file.lastModified,
    });
  } catch {
    return file;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** The longest side a profile or family picture is kept at (issue #24). */
export const AVATAR_MAX_SIDE = 512;

/**
 * A picture for a profile or family, as a small JPEG data URL. A phone photo
 * is several MB; sent as it is, it was over the server's 1 MB request limit
 * (issue #24). Resized to 512 px it's a few tens of KB.
 */
export async function avatarDataUrl(file: File): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error('Unreadable image'));
      element.src = url;
    });
    const target = fitWithin(
      image.naturalWidth,
      image.naturalHeight,
      AVATAR_MAX_SIDE
    ) ?? { width: image.naturalWidth, height: image.naturalHeight };
    const canvas = document.createElement('canvas');
    canvas.width = target.width;
    canvas.height = target.height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('No canvas');
    // JPEG has no transparency: put PNG pictures on white, not black.
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, target.width, target.height);
    context.imageSmoothingQuality = 'high';
    context.drawImage(image, 0, 0, target.width, target.height);
    return canvas.toDataURL('image/jpeg', JPEG_QUALITY);
  } finally {
    URL.revokeObjectURL(url);
  }
}
